import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Box, Stack, TextField } from '@mui/material';
import MenuButton from '../../Shared/MenuButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SocketContext from '../../../contexts/SocketContext';
import { AccueilContext } from '../../../contexts/AccueilContext';
import { AlertContext } from '../../../contexts/AlertContext';
import { GameContext } from '../../../contexts/GameContext';
import { useTranslation } from '../../../contexts/LanguageContext';
import { getRandomPseudo } from '../../../utils/randomPseudo';

const JoinGameButton = () => {
    const background = 'url(/images/buttons/menuButton1.png)';
    const backgroundDisabled = 'url(/images/buttons/menuButton1_disabled.png)';
    const backgroundHover = 'url(/images/buttons/menuButton1_hover.png)';

    const accueilContext = useContext(AccueilContext);
    const alertContext = useContext(AlertContext);
    const gameContext = useContext(GameContext);

    const [searchParams, setSearchParams] = useSearchParams();
    const [gameIdValue, setGameIdValue] = useState('');

    useEffect(() => {
        const joinCode = searchParams.get('join');
        if (joinCode) {
            setGameIdValue(joinCode);
            searchParams.delete('join');
            setSearchParams(searchParams, { replace: true });
        }
    }, []);

    const socketContext = useContext(SocketContext);
    const { language, t } = useTranslation();

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setGameIdValue(event.target.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !isDisabled()) {
            handleJoinGame();
        }
    };

    const navigate = useNavigate();

    const handleJoinGame = async () => {
        const name = accueilContext.playerName.trim() || getRandomPseudo(language);
        socketContext
            .getClient()
            .JoinGame(gameIdValue, name)
            .then((x) => {
                if (x.success == false) {
                    alertContext?.setAlertMessage(t('joinError'));
                    return;
                }

                gameContext.setGameId(gameIdValue);
                navigate(`/game/${gameIdValue}`);
            });
    };

    function isDisabled() {
        return !gameIdValue.trim();
    }

    return (
        <Stack
            spacing={{ xs: 2, md: 4 }}
            display="flex"
            direction={{ xs: 'column', md: 'row' }}
            sx={{
                width: '100%',
                height: '100%',
                alignItems: { xs: 'center', md: 'center' },
            }}
        >
            <MenuButton
                text={t('join')}
                buttonWidth={{ xs: '100%', md: '60%' }}
                textSize="h5"
                onClick={handleJoinGame}
                background={isDisabled() ? backgroundDisabled  : background}
                hoverBackground={backgroundHover}
                dynamicCursor={true}
                staticCursorImage={''}
                disabled={isDisabled()}
            />

            <Box
                display="flex"
                alignItems="center"
                sx={{
                    backgroundImage: 'url(/images/buttons/menuButton2.png)',
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    height: { xs: 52, md: 48 },
                    width: { xs: '70%', sm: '50%', md: '30%' },
                    '&:hover': {
                        backgroundImage: 'url(/images/buttons/menuButton2_hover.png), url(/images/buttons/menuButton2.png)'
                    },
                    borderRadius:1
                }}
            >
                <TextField
                    value={gameIdValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                    placeholder="Game code"
                    variant="standard"
                    margin="none"
                    inputProps={{ maxLength: 4, style: { fontSize: 20, textAlign: 'center', fontStyle: 'italic' } }}
                    sx={{ input: { color: 'black' }, width: '100%' }}
                    InputProps={{
                        disableUnderline: true
                    }}
                />
            </Box>
        </Stack>
    );
};

export default JoinGameButton;
